import { LitElement, html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";

import {
  SlhCoreActionButton,
  SlhCoreActionButtonIcon,
} from "@slhuicore/action-button";

import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { ActionButtonListDirection } from "@slhuicore/action-button/src/action-button.constants";
import { style } from "./assets/action-button.generated-style";

import { ACTION_BUTTON_LIST_SPACING } from "./action-button.constants";

/**
 * @CustomElement slh-action-button
 * @cssprop --slh-color-primary-shade-40 - Action button background color.
 * @cssprop --slh-color-neutral-shade-80 - Action button font color.
 * @cssprop --slh-color-neutral-shade-00 - Action dropdown background color. Scrollbar color. Scorollbar truck color.
 * @cssprop --slh-default-font-family - Action button font family.
 * @cssprop --slh-base-semi-bold-font-weight - Action button label font weight.
 * @cssprop --slh-base-xxs-line-height - Action button line height.
 * @cssprop --slh-base-m-font-size - Action button font size.
 * @cssprop --slh-color-primary-shade-60 - Action button hover background color.
 * @cssprop --slh-color-neutral - Action button hover color.
 * @cssprop --slh-color-neutral-shade-20 - Action button secondary background color.
 * @cssprop --slh-color-neutral-shade-40 - Scrollbar color, scrollbar truck color, scrollbar thumb color.
 * @cssprop --slh-color-neutral-shade-80-opacity-12 - Drop shadow inner color for elevated2.
 * @cssprop --slh-color-neutral-shade-80-opacity-16 - Drop shadow outer color for elevated2.
 * @cssprop --slh-color-outline - Focused element on tab border color.
 *
 * @slot default - List of slh-action-button-items
 * @slot label - plain text, label for action button
 */
@customElement("slh-action-button")
export default class SlhActionButton extends SlhCoreActionButton(LitElement) {
  static styles = style;

  /**
   * Add primary styling to the action button
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  primary: boolean = true;

  /**
   * Add secondary styling to the action button
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  secondary: boolean = false;

  listSpacing: number = ACTION_BUTTON_LIST_SPACING;

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  renderClassesButton(): ClassInfo {
    return {
      primary: this.primary,
      secondary: this.secondary,
      disabled: this.disabled,
    };
  }

  /**
   * Render icon template
   */
  // eslint-disable-next-line class-methods-use-this
  renderIcon(icon: SlhCoreActionButtonIcon): TemplateResult {
    return html`<slh-icon
      type="${icon.type}"
      color="${icon.color}"
    ></slh-icon>`;
  }

  getDefaultIcon(): SlhCoreActionButtonIcon {
    const type: string = this.getOpenState()
      ? IconType.ICO_DROPDOWN_UP
      : IconType.ICO_DROPDOWN_DOWN;
    return {
      type,
      color: "",
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.setElementNodeName("SLH-ACTION-BUTTON");
  }

  firstUpdated() {
    // @ts-ignore
    super.firstUpdated();

    setTimeout(() => {
      if (
        this.actionButtonContainer &&
        this.direction === ActionButtonListDirection.DIRECTION_DOWN
      ) {
        this.itemsTopPosition = `top: ${
          this.actionButtonContainer.offsetHeight + ACTION_BUTTON_LIST_SPACING
        }px`;
      }
    }, 0);
  }

  /**
   * @returns {string} render html template
   */
  render(): TemplateResult {
    return html` <div class="action-wrapper ${classMap(this.renderClasses())}">
      <div class="action-button ${classMap(this.renderClassesButton())}">
        ${this.renderPlaceholder()}
      </div>
      ${this.renderItems()}
    </div>`;
  }
}
